'use client';

import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Edit, Plus } from 'lucide-react';

interface Country {
  id: number;
  code: string;
  translations: Record<string, string[]>;
  created_at: string;
  updated_at: string;
}

interface Language {
  code: string;
  description: string;
}

const CountriesPage = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    code: '',
    translations: {} as Record<string, string[]>,
  });
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(['HU']);

  // Fetch countries és languages
  useEffect(() => {
    fetchCountries();
    fetchLanguages();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/countries');
      const data = await response.json();
      setCountries(data.countries || []);
    } catch (error) {
      console.error('Hiba az országok lekérésekor:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await fetch('/api/admin/languages');
      const data = await response.json();
      setLanguages(data.languages || []);
    } catch (error) {
      console.error('Hiba a nyelvek lekérésekor:', error);
    }
  };

  const openDialog = (country?: Country) => {
    if (country) {
      setIsEditing(true);
      setEditingId(country.id);
      setFormData({
        code: country.code,
        translations: { ...country.translations },
      });
      // Set selected languages based on existing translations
      setSelectedLanguages(['HU', ...Object.keys(country.translations).filter(lang => lang !== 'HU')]);
    } else {
      setIsEditing(false);
      setEditingId(null);
      setFormData({
        code: '',
        translations: { HU: [''] }, // Alapértelmezésben HU van egy üres néVel
      });
      setSelectedLanguages(['HU']);
    }
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setFormData({
      code: '',
      translations: {},
    });
  };

  const handleSave = async () => {
    if (!formData.code.trim()) {
      alert('Az ország kódja kötelező!');
      return;
    }

    try {
      const url = isEditing
        ? `/api/admin/countries/${editingId}`
        : '/api/admin/countries';
      const method = isEditing ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          country: {
            code: formData.code,
            translations: formData.translations,
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.error || 'Hiba a mentés során');
        return;
      }

      fetchCountries();
      closeDialog();
    } catch (error) {
      console.error('Hiba a mentés során:', error);
      alert('Hiba a mentés során');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Biztosan törölni szeretnéd ezt az országot?')) return;

    try {
      const response = await fetch(`/api/admin/countries/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.error || 'Hiba a törlés során');
        return;
      }

      fetchCountries();
    } catch (error) {
      console.error('Hiba a törlés során:', error);
      alert('Hiba a törlés során');
    }
  };

  const handleTranslationChange = (languageCode: string, index: number, value: string) => {
    const currentNames = formData.translations[languageCode] || [];
    const newNames = [...currentNames];
    newNames[index] = value;

    setFormData({
      ...formData,
      translations: {
        ...formData.translations,
        [languageCode]: newNames,
      },
    });
  };

  const handleAddAlias = (languageCode: string) => {
    const currentNames = formData.translations[languageCode] || [];
    setFormData({
      ...formData,
      translations: {
        ...formData.translations,
        [languageCode]: [...currentNames, ''],
      },
    });
  };

  const handleRemoveAlias = (languageCode: string, index: number) => {
    const currentNames = formData.translations[languageCode] || [];
    const newNames = currentNames.filter((_, i) => i !== index);

    if (newNames.length === 0 && languageCode === 'HU') {
      // HU-nál legalább egy érték kell
      return;
    }

    if (newNames.length === 0) {
      // Ha nincs több érték, távolítsd el a nyelvet
      const newLanguages = selectedLanguages.filter(lang => lang !== languageCode);
      setSelectedLanguages(newLanguages);
      const newTranslations = { ...formData.translations };
      delete newTranslations[languageCode];
      setFormData({
        ...formData,
        translations: newTranslations,
      });
    } else {
      setFormData({
        ...formData,
        translations: {
          ...formData.translations,
          [languageCode]: newNames,
        },
      });
    }
  };

  const handleAddLanguage = (languageCode: string) => {
    if (!selectedLanguages.includes(languageCode)) {
      setSelectedLanguages([...selectedLanguages, languageCode]);
      setFormData({
        ...formData,
        translations: {
          ...formData.translations,
          [languageCode]: [''],
        },
      });
    }
  };

  const handleRemoveLanguage = (languageCode: string) => {
    if (languageCode === 'HU') return; // HU-t nem lehet teljesen eltávolítani

    const newLanguages = selectedLanguages.filter(lang => lang !== languageCode);
    setSelectedLanguages(newLanguages);

    const newTranslations = { ...formData.translations };
    delete newTranslations[languageCode];
    setFormData({
      ...formData,
      translations: newTranslations,
    });
  };

  const getAvailableLanguagesToAdd = () => {
    return languages.filter(lang => !selectedLanguages.includes(lang.code));
  };

  if (loading) {
    return <div className="text-center py-8">Betöltés...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary">Országok</h1>
          <p className="mt-2 text-foreground/80">Kezeld az app által támogatott országokat és azok nyelveken használt neveit.</p>
        </div>
        <Button
          onClick={() => openDialog()}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Új ország
        </Button>
      </div>

      {countries.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-lg">
          <p className="text-foreground/60">Még nincs hozzáadott ország</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kód</TableHead>
                <TableHead>Fordítások</TableHead>
                <TableHead className="text-right">Műveletek</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {countries.map((country) => (
                <TableRow key={country.id}>
                  <TableCell className="font-medium">{country.code}</TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      {Object.entries(country.translations).map(([lang, names]) => (
                        <div key={lang} className="flex flex-wrap gap-2">
                          {(Array.isArray(names) ? names : [names]).map((name, idx) => (
                            <span
                              key={`${lang}-${idx}`}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-primary/10 text-primary"
                            >
                              <span className="font-bold mr-1">{lang}:</span>
                              {name}
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDialog(country)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(country.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Ország szerkesztése' : 'Új ország hozzáadása'}
            </DialogTitle>
            <DialogDescription>
              Add meg az ország kódját és a nyelveken használt neveit.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="code">Ország kódja</Label>
              <Input
                id="code"
                placeholder="pl. AT, HU"
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value.toUpperCase() })
                }
                maxLength={5}
                disabled={isEditing}
              />
            </div>

            <div className="space-y-2">
              <Label>Fordítások</Label>
              <div className="space-y-6">
                {selectedLanguages.map((langCode) => {
                  const langInfo = languages.find(l => l.code === langCode);
                  const names = formData.translations[langCode] || [''];
                  return (
                    <div key={langCode} className="border rounded-lg p-4 space-y-2 bg-muted/30">
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-semibold">
                          {langInfo?.description} ({langCode})
                          {langCode === 'HU' && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        {langCode !== 'HU' && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveLanguage(langCode)}
                            className="text-destructive hover:text-destructive"
                          >
                            ✕ Egész nyelv eltávolítása
                          </Button>
                        )}
                      </div>

                      <div className="space-y-2">
                        {names.map((name, idx) => (
                          <div key={idx} className="flex gap-2">
                            <Input
                              placeholder={`${langInfo?.description} név ${idx + 1}.`}
                              value={name}
                              onChange={(e) =>
                                handleTranslationChange(langCode, idx, e.target.value)
                              }
                              required={langCode === 'HU' && names.length === 1}
                            />
                            {names.length > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleRemoveAlias(langCode, idx)}
                                className="text-destructive"
                              >
                                ✕
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddAlias(langCode)}
                        className="w-full gap-2"
                      >
                        <Plus className="h-3 w-3" />
                        +{langCode} alias hozzáadása
                      </Button>
                    </div>
                  );
                })}
              </div>

              {getAvailableLanguagesToAdd().length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-sm font-medium mb-3 block">Új nyelv hozzáadása:</Label>
                  <div className="flex flex-wrap gap-2">
                    {getAvailableLanguagesToAdd().map((lang) => (
                      <Button
                        key={lang.code}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddLanguage(lang.code)}
                        className="gap-2"
                      >
                        <Plus className="h-3 w-3" />
                        +{lang.code}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeDialog}>
                Mégse
              </Button>
              <Button onClick={handleSave}>
                {isEditing ? 'Frissítés' : 'Hozzáadás'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CountriesPage;